import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "reactstrap";
import { useParams, useHistory } from "react-router-dom";
import { acceptQuote, getQuoteCheckout } from "../services/QuoteServices";
import { toast } from "react-toastify";
import { truncateString, formatPhoneNumber, formatAddress } from "../utils";

export default function QuoteCheckout() {
  const { id } = useParams();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPlaceOrder, setIsLoadingPlaceOrder] = useState(false);
  const [customerDetails, setCustomerDetails] = useState([]);
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [cardID, setCardID] = useState("");
  const [selectCardMessage, setSelectCardMessage] = useState(false);

  const fetchCheckoutData = useCallback(() => {
    setIsLoading(true);

    getQuoteCheckout(id)
      .then((res) => {
        setCustomerDetails(res.data.customerData);
        setQuoteDetails(res.data.quoteData);
      })
      .catch((err) => {
        toast.error("Failed to fetch quote checkout data");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  useEffect(() => {
    fetchCheckoutData();
  }, [fetchCheckoutData]);

  const handlePlaceOrder = () => {
    if (!cardID) {
      setSelectCardMessage(true);
      return;
    }

    setIsLoadingPlaceOrder(true);
    acceptQuote(id, {
      cardId: cardID,
    })
      .then((res) => {
        toast.success("Order placed successfully");
        history.push("/quote");
      })
      .catch((err) => {
        toast.error("Failed to place order.\nPlease try again later.");
      })
      .finally(() => {
        setIsLoadingPlaceOrder(false);
      });
  };

  if (isLoading) {
    return (
      <div className="col-md-12">
        <div className="custom-loader">
          <Spinner animation="border" />
        </div>
        <p className="text-center mt-3">Please wait...</p>
      </div>
    );
  }

  return (
    <>
      <div className="row">
        {/* to cover the column space */}
        <div className="col-lg-2"></div>
        <div className="col-lg-8 checkout_detail_column">
          <div className="checkout-details">
            <div className="d-flex flex-wrap align-items-center justify-content-between shipping_header_checkout">
              <h3>Order Detail</h3>
            </div>
            {/* Order Details Table */}
            <div
              className="order-box table-responsive"
              style={{ "overflow-x": "auto" }}
            >
              <table className="table table-responsive-1 table-bordered">
                <thead className="">
                  <tr className="">
                    <th style={{ width: "200px" }}>Name</th>
                    <th style={{ width: "100px" }}>Price</th>
                    <th style={{ width: "50px" }}>Qty</th>
                    <th style={{ width: "100px" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quoteDetails?.items?.length > 0 &&
                    quoteDetails?.items?.map((item) => {
                      return (
                        <tr className="">
                          <td style={{ width: "200px" }}>
                            {truncateString(item.product_name, 100)}
                          </td>
                          <td style={{ width: "100px" }}>
                            $
                            {parseFloat(item.unit_price).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              },
                            )}
                          </td>
                          <td style={{ width: "50px" }}>{item.quantity}</td>
                          <td style={{ width: "100px" }}>
                            $
                            {parseFloat(
                              item.quantity * item.unit_price,
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Order Summary */}
            <div className="order-box table-responsive">
              <ul className="sub-total">
                <li>
                  Total Price
                  <span className="count text-right">
                    $
                    {parseFloat(quoteDetails?.sumOfTotalPrices).toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                </li>
                <li>
                  Total Tax
                  <span className="count text-right">
                    $
                    {parseFloat(quoteDetails?.tax).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </li>
                <li>
                  Total Shipping
                  <span className="count text-right">
                    $
                    {parseFloat(quoteDetails?.shipping).toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                </li>
              </ul>
              <ul className="sub-total">
                <li>
                  Subtotal
                  <span className="count text-right">
                    $
                    {parseFloat(quoteDetails?.subtotal).toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                  </span>
                </li>
              </ul>
            </div>

            {/* Shipping Address */}
            <div className="shipping_header_checkout mb-4">
              <h3>Shipping Address</h3>
              <div>
                {customerDetails.length > 0
                  ? customerDetails?.[0].card_holder_name +
                    ", " +
                    " " +
                    formatPhoneNumber(customerDetails?.[0].phone_number)
                  : null}
              </div>

              <div>
                {quoteDetails?.address2 && quoteDetails?.address2 + ", "}
                {formatAddress(
                  quoteDetails?.street,
                  quoteDetails?.city,
                  quoteDetails?.state,
                  quoteDetails?.zip_code,
                )}
              </div>
            </div>

            {/* Choose Card */}
            <div className="checkout_card_list">
              <div className="d-flex flex-wrap align-items-center justify-content-between shipping_header_checkout">
                <h3>Choose Card</h3>
              </div>
              {customerDetails.length > 0 ? (
                <ul>
                  {customerDetails?.map((card, index) => {
                    return (
                      <li>
                        <div class="inputGroup">
                          <input
                            id={`radio_card_id_${index}`}
                            name="radio"
                            type="radio"
                            value={card.id}
                            defaultChecked={card.default_card}
                            onChange={() => {
                              setSelectCardMessage(false);
                              setCardID(card.id);
                            }}
                          />
                          <label for={`radio_card_id_${index}`}>
                            <h5>{card.card_holder_name}</h5>
                            <p>**** **** **** {card.card_number}</p>
                          </label>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>
                  You have not added a card for payment. Please add a card to
                  continue.
                </p>
              )}
            </div>

            {/* Select Card Message */}
            {selectCardMessage && (
              <div className="select-card-message">Please select a card</div>
            )}

            {/* Place Order Button */}
            <div className="payment-box">
              <div className="text-right">
                <button
                  type="submit"
                  className="btn-solid btn"
                  onClick={(e) => handlePlaceOrder(e)}
                  disabled={isLoadingPlaceOrder}
                >
                  {isLoadingPlaceOrder ? "Placing Order..." : "Place Order"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* to cover the column space */}
        <div className="col-lg-2"></div>
      </div>
    </>
  );
}
